import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import iconRedCross from 'assets/icons/cross.svg';
import iconGreenCheck from 'assets/icons/tick.svg';
import Button from 'components/Button/Button';
import { PasswordRequirementsAndMatch } from 'components/PasswordRequirementsAndMatch/PasswordRequirementsAndMatch';
import { TextInput } from 'components/TextInput/TextInput';
import useAppLanguage from 'hooks/useAppLanguage';
import { useDebouncedState } from 'hooks/useDebouncedState';
import { isEmpty } from 'lodash';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useValidatePromoCodeQuery } from 'store/slices/profile-api';
import { EVENTS, SIGNUP_STEPS, trackEvent } from 'utils/analytics';
import { isValidEmail } from 'utils/validation';
import { z } from 'zod';
import { useSignupWithEmailAndPasswordMutation } from '../api/auth-api';
import { RegisterUserState } from './_authenticated/types/RegisterUserState';
import messages from './messages';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { sendEmailOtpPin } from 'store/otp/otp-actions';
import { otpProcesses } from 'utils/constants';

export const Route = createFileRoute('/auth/signup/')({
  component: RegisterUser,
  validateSearch: zodValidator(
    z.object({
      promoCode: z.string().optional(),
      email: z.string().optional(),
    }),
  ),
});

const minPasswordLength = 6;
const maxPasswordLength = 128;

function RegisterUser() {
  const { promoCode, email } = Route.useSearch();
  const dispatch = useAppDispatch();
  const [state, setState] = React.useState<RegisterUserState>({
    promoCode,
    hasPromoCode: !!promoCode,
    email,
  });
  const debouncedPromoCode = useDebouncedState(state.promoCode, 1000);

  const navigate = useNavigate();
  const language = useAppLanguage();
  const registerUserMessages = messages[language].registerUser;

  const [signup, signupData] = useSignupWithEmailAndPasswordMutation();
  const { data: validatePromoCodeResponse, isFetching: validatingPromoCode } =
    useValidatePromoCodeQuery(
      { code: debouncedPromoCode! },
      { skip: !state.promoCode || (debouncedPromoCode?.length ?? 0) < 1 },
    );

  const promoCodeIsValid =
    isEmpty(state.promoCode) || validatePromoCodeResponse?.valid;

  function checkPassword() {
    // True otherwise will always display warning even when the page is just loading in
    if (!state.newPassword) return true;
    // TODO: consider adding password strength indicator, as in mobile App
    if (state.newPassword?.length < minPasswordLength) return false;
    if (state.newPassword?.length > maxPasswordLength) return false;
    if (!/[a-z]/.test(state.newPassword)) return false;
    if (!/[A-Z]/.test(state.newPassword)) return false;
    if (!/[0-9]/.test(state.newPassword)) return false;

    return true;
  }

  function isStateValid(): boolean {
    if (!state.email) return false;
    if (!state.newPassword) return false;
    if (import.meta.env.VITE_ENV === 'production' && !state.recaptcha) {
      return false;
    }
    if (!promoCodeIsValid) return false;
    if (!checkPassword()) return false;
    return isValidEmail(state.email);
  }

  async function onRegisterUser(event: React.SyntheticEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!isStateValid()) return;

    const res = await signup({
      email: state.email!,
      password: state.newPassword!,
      recaptchaToken: state.recaptcha!,
      promoCode: state.promoCode!,
    });

    if (!res.error) {
      trackEvent(EVENTS.REGISTER, {
        step: SIGNUP_STEPS.SIGN_UP,
        userId: res?.data.userId ?? undefined,
      });
      dispatch(sendEmailOtpPin(otpProcesses.SIGNUP_ENABLE_EMAIL_OTP));
      navigate({ from: Route.fullPath, to: './verify-email' });
    }
  }
  return (
    <div>
      <form onSubmit={onRegisterUser} autoComplete="off">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="font-semibold leading-9 mt-6 text-2.5x1 text-black text-center tracking-wide">
            {registerUserMessages.heading}
          </h2>
          <p className="font-normal mt-3 text-center text-gray-900 text-lg tracking-wide mx-6 sm:mx-0">
            {registerUserMessages.subHeading}
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="m-6">
            <TextInput
              name="email"
              type="email"
              label={registerUserMessages.emailLabel}
              value={state.email}
              onChange={value => setState({ ...state, email: value as string })}
              isRegisterUserEmail
            />
            <TextInput
              name="password"
              type="password"
              label={registerUserMessages.passwordLabel}
              onChange={value =>
                setState({ ...state, newPassword: value as string })
              }
              warning={!checkPassword()}
              value={state?.newPassword}
              stopAutofill
            />
            <PasswordRequirementsAndMatch
              isPasswordMatch={false}
              passwordState={state}
            />

            <div className="flex items-start py-4">
              <div className="flex items-center h-5 ml-1">
                <input
                  data-browsing-ignore
                  id="hasReferralCode"
                  type="checkbox"
                  className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out rounded"
                  checked={state.hasPromoCode}
                  onChange={e => {
                    setState({
                      ...state,
                      hasPromoCode: e.target.checked,
                      promoCode: '',
                    });
                  }}
                />
              </div>
              <div className="ml-3 text-sm leading-5 font-normal">
                <label
                  htmlFor="hasReferralCode"
                  className="font-normal text-gray-700">
                  {registerUserMessages.havePromoCodeLabel}
                </label>
              </div>
            </div>

            {state.hasPromoCode && (
              <div className="mt-3 relative">
                <div className="d-flex flex-row">
                  <TextInput
                    name="referralCode"
                    type="referralCode"
                    label={registerUserMessages.enterCodeLabel}
                    warning={
                      !(validatePromoCodeResponse?.valid ?? false) &&
                      !isEmpty(state.promoCode) &&
                      !validatingPromoCode
                    }
                    value={state.promoCode}
                    onChange={value => setState({ ...state, promoCode: value })}
                    onCompositionEnd={value =>
                      setState({ ...state, promoCode: value as string })
                    }
                  />
                  {!isEmpty(state.promoCode) && !validatingPromoCode && (
                    <div
                      style={{
                        position: 'absolute',
                        right: '0rem',
                        top: '1.80rem',
                      }}>
                      {validatePromoCodeResponse?.valid && (
                        <img
                          style={{ width: '1rem' }}
                          src={iconGreenCheck}
                          alt={'success'}
                        />
                      )}
                    </div>
                  )}
                </div>
                {!validatePromoCodeResponse?.valid &&
                !isEmpty(state.promoCode) &&
                !validatingPromoCode ? (
                  <div className="text-error">
                    {registerUserMessages.codeNotRecognizedLabel}
                  </div>
                ) : null}
              </div>
            )}

            <div className="mt-6 flex items-center justify-between flex-col">
              <ReCAPTCHA
                sitekey={import.meta.env.VITE_GOOGLE_RECAPTCHA_KEY as string}
                onChange={value => {
                  setState({ ...state, recaptcha: value ?? undefined });
                }}
              />
            </div>

            <div className="mt-6">
              <span className="block w-full rounded-md">
                <Button
                  loading={signupData.isLoading}
                  onClick={onRegisterUser}
                  disabled={!isStateValid()}
                  w-full
                  flex
                  justify-center
                  py-2
                  px-4
                  border
                  border-transparent
                  text-sm
                  font-normal
                  rounded-md
                  text-white
                  transition
                  duration-150
                  ease-in-out
                  bg-vivid-blue
                  type="submit">
                  {registerUserMessages.continueButton}
                </Button>
              </span>
            </div>

            {/* <div className="flex flex-row align-middle justify-items-center items-center my-2">
                            <hr className="flex-grow" />
                            <div className="mx-2">OR</div>
                            <hr className="flex-grow" />
                        </div> */}

            {/* <SSO
                            flow="signup"
                            loadingProvider={auth.ssoLoading}
                            onSuccess={(key: SSOAuthProviderKey, response) => {
                                console.log(`LoginUser.tsx got SSO (${key}) response:`, response);
                                dispatch(
                                    registerWithProviderAction(
                                        {
                                            provider: key,
                                            clientApplicationSsoCode: `DASHBOARD_${key}`,
                                            ssoJwt: response?.user?.accessToken,
                                        }
                                        // (res: any) => {
                                        //     }
                                    )
                                );
                            }}
                            onError={(error) => {
                                console.log(`Error on login`, error);
                            }}
                        /> */}

            {signupData.error ? (
              <div className="mt-6">
                <span className="block text-center text-error">
                  {signupData.error.message}
                </span>
              </div>
            ) : null}

            <div className="mt-6">
              <span className="block text-center">
                {registerUserMessages.alreadyHaveAnAccountLabel}&nbsp;
                <Link
                  to="/auth/login"
                  search={{ email: state.email }}
                  className="font-semibold text-primary-button underline hover:no-underline">
                  {registerUserMessages.loginLink}
                </Link>
              </span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
